import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch } from '@/app/hooks'
import apiClient from '@/lib/api/apiClient'
import { useUserRole } from '@/hooks/useUserRole'
import { showSuccess, showApiError, showValidationMessage } from '@/lib/utils/notifications'
import { shouldShowStaffListActionColumn } from '@/lib/utils/staffListActionColumn'
import { navigateToUserManagementEdit, navigateToUserManagementList } from '@/lib/utils/userManagementNavigation'
import { staffImpersonateAndRedirect, rollbackStaffImpersonationAttempt } from '@/lib/auth/staffImpersonation'
import type { CompanyStaffMember } from '@/features/company/types'
import { getSelectableUserIdsForEmail, resolvePropertyCompanyId } from '../utils/propertyContactsLogic'
import type { PropertyContactsTabProps } from '../types'

export function usePropertyContactsTab({
  property,
  propertyId,
  onRefresh,
  autoOpenStaffTransfer,
  onAutoOpenStaffTransferConsumed,
}: PropertyContactsTabProps) {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { hasPolicy, isLibertyRole, hasWriteAccess, roleName } = useUserRole()

  const canEdit = hasPolicy(['property_contact_edit'])
  const canLogCall = hasPolicy(['property_crm_log_call'])
  const canImpersonate = hasPolicy(['user_management_impersonate_pmc_role'])
  const canRemoveRole = hasPolicy(['user_management_edit_role_remove'])
  const companyId = resolvePropertyCompanyId(property)
  const canShowStaffActions = shouldShowStaffListActionColumn(
    roleName,
    hasWriteAccess('user_management'),
    null,
  )

  const [propertyStaff, setPropertyStaff] = useState<CompanyStaffMember[]>([])
  const [staffLoading, setStaffLoading] = useState(false)
  const [removeConfirm, setRemoveConfirm] = useState<{ isOpen: boolean; staff: CompanyStaffMember | null }>({
    isOpen: false,
    staff: null,
  })
  const [deleteUserConfirm, setDeleteUserConfirm] = useState<{ isOpen: boolean; userId: number | null }>({
    isOpen: false,
    userId: null,
  })
  const [deleteOrDisableConfirm, setDeleteOrDisableConfirm] = useState<{
    isOpen: boolean
    type: number
    userId: number | null
  }>({ isOpen: false, type: 0, userId: null })
  const [welcomeConfirm, setWelcomeConfirm] = useState<{ isOpen: boolean; staff: CompanyStaffMember | null }>({
    isOpen: false,
    staff: null,
  })
  const [showSelectBox, setShowSelectBox] = useState(false)
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([])
  const [composeConfirmOpen, setComposeConfirmOpen] = useState(false)
  const [emailBroadcastOpen, setEmailBroadcastOpen] = useState(false)
  const [transferOpen, setTransferOpen] = useState(false)
  const [addUserOpen, setAddUserOpen] = useState(false)
  const [logCallStaff, setLogCallStaff] = useState<CompanyStaffMember | null>(null)

  const loadStaff = useCallback(async () => {
    if (!propertyId) return
    setStaffLoading(true)
    try {
      const res = await apiClient.get('/api/property_staff', {
        params: { property_id: propertyId, sortBy: 'desc' },
      })
      setPropertyStaff(Array.isArray(res.data) ? res.data : [])
    } catch {
      setPropertyStaff([])
    } finally {
      setStaffLoading(false)
    }
  }, [propertyId])

  useEffect(() => {
    void loadStaff()
  }, [loadStaff])

  useEffect(() => {
    if (!autoOpenStaffTransfer) return
    setTransferOpen(true)
    onAutoOpenStaffTransferConsumed?.()
  }, [autoOpenStaffTransfer, onAutoOpenStaffTransferConsumed])

  const selectableUserIdsForEmail = useMemo(
    () => getSelectableUserIdsForEmail(propertyStaff),
    [propertyStaff],
  )

  const allEmailRowsSelected =
    selectableUserIdsForEmail.length > 0 &&
    selectableUserIdsForEmail.every((id) => selectedUserIds.includes(id))
  const someEmailRowsSelected = selectableUserIdsForEmail.some((id) => selectedUserIds.includes(id))

  const toggleSelectAllEmail = useCallback(() => {
    if (!selectableUserIdsForEmail.length) return
    setSelectedUserIds((prev) => {
      const every = selectableUserIdsForEmail.every((id) => prev.includes(id))
      if (every) return prev.filter((id) => !selectableUserIdsForEmail.includes(id))
      return Array.from(new Set([...prev, ...selectableUserIdsForEmail]))
    })
  }, [selectableUserIdsForEmail])

  const cancelEmail = useCallback(() => {
    setShowSelectBox(false)
    setSelectedUserIds([])
  }, [])

  const onEmailBroadcastDone = useCallback(() => {
    setEmailBroadcastOpen(false)
    setShowSelectBox(false)
    setSelectedUserIds([])
  }, [])

  const onTransferSuccess = useCallback(() => {
    void loadStaff()
    onRefresh?.()
  }, [loadStaff, onRefresh])

  const handleImpersonate = async (roleId: number) => {
    try {
      await staffImpersonateAndRedirect(dispatch, roleId, { fallbackPropertyId: propertyId })
    } catch (err) {
      rollbackStaffImpersonationAttempt()
      showApiError(err)
    }
  }

  const handleRemoveRoleClick = (staff: CompanyStaffMember) => {
    if (propertyStaff.length === 1) {
      showValidationMessage(
        'There are no other employees assigned to this property. Please add a new contact before removing this user.',
        'Sorry, Unable To Remove User!',
      )
      return
    }
    setRemoveConfirm({ isOpen: true, staff })
  }

  const executeRemoveRole = async () => {
    const staff = removeConfirm.staff
    setRemoveConfirm({ isOpen: false, staff: null })
    if (!staff?.user_id || staff.role_id == null) return
    try {
      const res = await apiClient.delete(`/api/user/${staff.user_id}/roles/${staff.role_id}`)
      const remainingRoles = res.data as number
      showSuccess('User Role Removed Successfully!')
      void loadStaff()
      if (remainingRoles <= 0) {
        setDeleteUserConfirm({ isOpen: true, userId: Number(staff.user_id) })
      }
    } catch (err) {
      showApiError(err)
    }
  }

  const handleDeleteUserChoice = (choice: boolean | string) => {
    const userId = deleteUserConfirm.userId
    setDeleteUserConfirm({ isOpen: false, userId: null })
    if (!userId) return
    if (choice === true) {
      setDeleteOrDisableConfirm({ isOpen: true, type: 1, userId })
    } else if (choice === false) {
      setDeleteOrDisableConfirm({ isOpen: true, type: 2, userId })
    }
  }

  const executeDeleteOrDisable = async () => {
    const { type, userId } = deleteOrDisableConfirm
    setDeleteOrDisableConfirm({ isOpen: false, type: 0, userId: null })
    if (!userId) return
    try {
      if (type === 1) {
        await apiClient.delete(`/api/user/${userId}/delete`)
      } else {
        await apiClient.delete(`/api/user/${userId}`)
      }
      showSuccess('User Action Updated Successfully!')
      void loadStaff()
    } catch (err) {
      showApiError(err)
    }
  }

  const executeWelcomeEmail = async () => {
    const staff = welcomeConfirm.staff
    setWelcomeConfirm({ isOpen: false, staff: null })
    if (!staff?.role_id) return
    try {
      await apiClient.get(`/api/send_welcome_email/${staff.role_id}`)
      showSuccess('Welcome Email Sent Successfully!')
    } catch (err) {
      showApiError(err)
    }
  }

  const toggleUserSelection = (userId: number) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId],
    )
  }

  const handleComposeClick = () => {
    if (!selectedUserIds.length) return
    setComposeConfirmOpen(true)
  }

  const onComposeConfirmed = () => {
    setComposeConfirmOpen(false)
    setEmailBroadcastOpen(true)
  }

  const handleUserManagement = () => {
    if (property) {
      navigateToUserManagementList(navigate, {
        scope: 'property',
        propertyId,
        propertyName: property.property_name != null ? String(property.property_name) : '',
        companyId,
        companyName: property.company_name != null ? String(property.company_name) : '',
      })
      return
    }
    navigateToUserManagementList(navigate)
  }

  const handleEditUser = (userId: string | number) => {
    navigateToUserManagementEdit(navigate, userId)
  }

  return {
    property,
    propertyId,
    companyId,
    canEdit,
    canLogCall,
    canImpersonate,
    canRemoveRole,
    canShowStaffActions,
    isLibertyRole,
    propertyStaff,
    staffLoading,
    showSelectBox,
    setShowSelectBox,
    selectedUserIds,
    selectableUserIdsForEmail,
    allEmailRowsSelected,
    someEmailRowsSelected,
    toggleSelectAllEmail,
    toggleUserSelection,
    cancelEmail,
    handleComposeClick,
    handleUserManagement,
    handleEditUser,
    handleImpersonate,
    handleRemoveRoleClick,
    transferOpen,
    setTransferOpen,
    addUserOpen,
    setAddUserOpen,
    logCallStaff,
    setLogCallStaff,
    loadStaff,
    onTransferSuccess,
    composeConfirmOpen,
    setComposeConfirmOpen,
    onComposeConfirmed,
    emailBroadcastOpen,
    setEmailBroadcastOpen,
    onEmailBroadcastDone,
    removeConfirm,
    setRemoveConfirm,
    executeRemoveRole,
    deleteUserConfirm,
    handleDeleteUserChoice,
    deleteOrDisableConfirm,
    setDeleteOrDisableConfirm,
    executeDeleteOrDisable,
    welcomeConfirm,
    setWelcomeConfirm,
    executeWelcomeEmail,
  }
}
